// packages import
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { FC, useMemo, useState } from 'react'

// component import
import { CustomModal } from '../../common/CustomModal'

// other imports
import {
  PaymentMethod,
  usePayableVoidPreviewQuery,
  useVoidPayableMutation,
  VoidedExpenseOutcome,
  VoidPreviewItem
} from '../../../../generated'
import { SOMETHING_WENT_WRONG } from '../../../constants'
import { formatCurrency } from '../../../utils'

interface VoidPayableDialogProps {
  payable: { id: string; voucherNo?: string | null; paymentMethod?: PaymentMethod | null }
  onClose: () => void
  onVoided: () => void
}

/**
 * The one Void action, for both the payables list and the payable detail page. Before confirming it
 * shows what will happen to each matter expense of the cheque, because that now depends on the bill
 * the expense is on (see PayableVoidCreditService on the API):
 *  - not billed        → the expense entry is deleted
 *  - on a pre-bill     → it is taken off the pre-bill and deleted
 *  - on a sent bill    → it stays on the bill, and the client is credited what they paid and relieved
 *                        of what they still owe
 * On a split bill with no client on the payable line, the user picks which client gets the credit.
 */
export const VoidPayableDialog: FC<VoidPayableDialogProps> = ({ payable, onClose, onVoided }) => {
  const [clientChoices, setClientChoices] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const creditClients = useMemo(
    () => Object.entries(clientChoices).map(([caseId, clientId]) => ({ caseId, clientId })),
    [clientChoices]
  )

  const { data, loading: loadingPreview } = usePayableVoidPreviewQuery({
    variables: { id: payable.id, creditClients: creditClients.length ? creditClients : undefined },
    fetchPolicy: 'network-only',
    onError: previewError => setError(previewError.message || SOMETHING_WENT_WRONG)
  })
  const items = data?.payableVoidPreview?.items ?? []

  const [voidPayable, { loading: voiding }] = useVoidPayableMutation({
    // Guard messages name the bill or the reconciliation to revert, so they reach the user verbatim.
    onError: voidError => setError(voidError.message || SOMETHING_WENT_WRONG),
    onCompleted: result => {
      if (result?.voidPayable?.response?.status === 200) onVoided()
    }
  })

  const isCheque = payable.paymentMethod === PaymentMethod.Cheque
  const hasBilledCost = items.some(item => item.outcome === VoidedExpenseOutcome.FinalBill)
  const hasPreBill = items.some(item => item.outcome === VoidedExpenseOutcome.PreBill)
  const unresolved = items.some(item => item.needsClientChoice)

  const intro = hasBilledCost
    ? `Void ${payable.voucherNo}? The check is reversed with an offsetting journal entry dated today. ` +
      'Its expense stays on the bill the client was sent, and the client is made whole as shown below.'
    : isCheque
    ? `Void ${payable.voucherNo}? If its accounting period is still open, this deletes the related bank ` +
      'payments and journal entries; if it has closed, the payment is reversed with an offsetting journal ' +
      `entry dated today instead. ${
        hasPreBill ? 'Expenses on a pre-bill are taken off it, and every' : 'Every'
      } matter expense entry below is deleted.`
    : `Void ${payable.voucherNo}? This will delete all the related bank payments, expense and journal entries.`

  return (
    <CustomModal title="Void Payable" isOpen handleClose={onClose} maxWidth="sm">
      <Typography variant="body1" sx={{ mb: 2 }}>
        {intro}
      </Typography>

      {loadingPreview && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        items.map(item => (
          <PreviewLine
            key={item.entryId}
            item={item}
            chosenClientId={clientChoices[item.caseId]}
            onChooseClient={clientId => {
              setError(null)
              setClientChoices(prev => ({ ...prev, [item.caseId]: clientId }))
            }}
          />
        ))
      )}

      {error && (
        <MuiAlert severity="error" sx={{ mb: 1 }}>
          {error}
        </MuiAlert>
      )}

      <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={voiding}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={voiding || loadingPreview || unresolved}
          endIcon={voiding && <CircularProgress size={20} color="inherit" />}
          onClick={() => {
            setError(null)
            voidPayable({ variables: { id: payable.id, creditClients: creditClients.length ? creditClients : undefined } })
          }}
        >
          Void
        </Button>
      </DialogActions>
    </CustomModal>
  )
}

const PreviewLine: FC<{
  item: VoidPreviewItem
  chosenClientId?: string
  onChooseClient: (clientId: string) => void
}> = ({ item, chosenClientId, onChooseClient }) => {
  const matter = `Matter ${item.matterNumber ?? ''}`.trim()

  if (item.outcome === VoidedExpenseOutcome.Unbilled) {
    return <Typography variant="body2" sx={{ mb: 1 }}>{`${matter}: unbilled expense of ${formatCurrency(item.amount)} is deleted.`}</Typography>
  }

  if (item.outcome === VoidedExpenseOutcome.PreBill) {
    return (
      <Typography variant="body2" sx={{ mb: 1 }}>
        {`${matter}: expense of ${formatCurrency(item.amount)} is removed from pre-bill #${item.billId}.`}
      </Typography>
    )
  }

  const outcomes = [
    item.creditAmount > 0 ? `${formatCurrency(item.creditAmount)} credited to the client (applied to the next bill)` : null,
    item.settlementAmount > 0 ? `${formatCurrency(item.settlementAmount)} still owed is taken off bill #${item.billId}` : null,
    item.uncollectedAmount > 0 ? `${formatCurrency(item.uncollectedAmount)} was already written off, so nothing is due back` : null
  ].filter(Boolean)

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2">
        {`${matter}: expense of ${formatCurrency(item.amount)} is on bill #${item.billId}.`}
        {!item.needsClientChoice && outcomes.length > 0 && ` ${outcomes.join('; ')}.`}
      </Typography>

      {(item.needsClientChoice || (item.clientOptions.length > 1 && chosenClientId)) && (
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel id={`void-client-${item.entryId}`}>Client who receives the credit</InputLabel>
          <Select
            labelId={`void-client-${item.entryId}`}
            label="Client who receives the credit"
            value={chosenClientId ?? ''}
            onChange={event => onChooseClient(String(event.target.value))}
          >
            {item.clientOptions.map(client => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  )
}
